import {FC, useCallback, useEffect, useState} from 'react'
import {useLocation} from 'react-router-dom'
import {ErrorBoundary, FallbackProps} from 'react-error-boundary'
import GridViewerManager from '../modules/widgets/components/external-viewer/grid-viewer-manager'
import TabViewerManager from '../modules/widgets/components/external-viewer/tab-viewer-manager'
import {createRequestViewerMessage} from '../modules/widgets/components/external-viewer/handler-structures'
import {useCognitoAuth} from '../modules/auth'

type ViewerLayoutMode = 'grid' | 'tabs'

const LAYOUT_PREF_KEY = 'viewer-layout-mode'

function getLayoutPreference(): ViewerLayoutMode {
  const stored = localStorage.getItem(LAYOUT_PREF_KEY)
  return stored === 'grid' ? 'grid' : 'tabs'
}

function ErrorFallback({error, resetErrorBoundary}: FallbackProps) {
  return (
    <div role='alert' className='alert alert-danger'>
      <h4>Something went wrong:</h4>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary} className='btn btn-primary'>
        Try again
      </button>
    </div>
  )
}

function refreshPage() {
  window.location.reload()
}

const WorkflowsPage: FC = () => {
  const location = useLocation()
  const {currentUser} = useCognitoAuth()
  const [layoutMode, setLayoutMode] = useState<ViewerLayoutMode>(getLayoutPreference)
  const viewerSessionKey = currentUser?.identity_id || 'anon'

  const toggleLayout = useCallback(() => {
    setLayoutMode((prev) => {
      const next: ViewerLayoutMode = prev === 'grid' ? 'tabs' : 'grid'
      localStorage.setItem(LAYOUT_PREF_KEY, next)
      return next
    })
  }, [])

  useEffect(() => {
    if (location.state?.openViewer && location.state?.viewerConfig) {
      setTimeout(() => {
        window.postMessage(createRequestViewerMessage(location.state.viewerConfig))
      }, 100)

      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onReset={refreshPage}>
      <div
        className='ob-page'
        style={{
          paddingTop: 'var(--ob-spacing-6)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* Header */}
        <div className='card-header border-0 pt-5' style={{minHeight: '110px', flexShrink: 0}}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'start'}}>
              <h3>Workflows</h3>
              <h5 style={{color: 'grey', alignContent: 'center'}}>
                View and manage your workflow files
              </h5>
            </div>

            <button
              className='btn btn-sm btn-link'
              onClick={toggleLayout}
              title={layoutMode === 'grid' ? 'Switch to tab view' : 'Switch to grid view'}
              style={{padding: 0, border: 'none', boxShadow: 'none', color: 'var(--ob-gray-600, #6c757d)'}}
            >
              <i className={`fas ${layoutMode === 'grid' ? 'fa-folder' : 'fa-th-large'}`}></i>
            </button>
          </div>
        </div>

        {/* Workflows Content */}
        <div
          className='ob-page__section'
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            gap: 'var(--ob-spacing-6)',
          }}
        >
          {layoutMode === 'grid' ? (
            <GridViewerManager
              key={`workflow-grid-${viewerSessionKey}`}
              title='Workflows'
              viewerType='workflow'
            />
          ) : (
            <TabViewerManager
              key={`workflow-tabs-${viewerSessionKey}`}
              title='Workflows'
              viewerType='workflow'
            />
          )}
        </div>
      </div>
    </ErrorBoundary>
  )
}

export default WorkflowsPage
